import {
  ADD_NOT_FINISHED,
  useAddToBagIntent,
  type AddToBagOutcome,
} from "./modules/AddToBagButton/useAddToBagIntent";
import type { CheckoutStartOutcome } from "./modules/BagButton/checkoutStart";
import { useConciergeWidget } from "./modules/Concierge/useConciergeWidget";
import { useStoreLocatorIntent } from "./modules/StoreLocator/useStoreLocatorIntent";
import BagDrawer from "./modules/BagButton/BagDrawer";
import { queryClient } from "./api/cache/queryClient";
import { activeCartsQueryOptions } from "./api/cache/cartQueryOptions";
import { CachedQueryKeys } from "./api/cache/cachedQueryKeys";
import { sumAllItemQuantities } from "./utils";
import { HOST_DOMAIN } from "./utils/domain";
import { authReady } from "./utils/authReady";
import type { UserProfile } from "./redux/types";
import {
  getAuthorizationHeader as amplifyAuthorizationHeader,
  redeemAndApplyLoginCode as amplifyRedeemLoginCode,
} from "@brandclub/common-ui";

/**
 * Imperative host APIs exposed on `window.BCCheckoutBar` via the
 * webpack `library: "BCCheckoutBar"` + `libraryTarget: "var"` config.
 *
 * These let a host page (e.g. the Next.js `@brandclub/pages` renderer)
 * trigger shop-app UI without relying on DOM anchor classnames.
 */

/**
 * Take the host's already-started Mixpanel SDK. The host offers it at mount
 * too; this entry point exists for the load order where this bundle mounted
 * first and the instance arrives afterwards.
 */
export { adoptHostMixpanel } from "./analytics/hostMixpanel";

export function openStoreLocator(args: { stacklineSku: string }): void {
  if (!args?.stacklineSku) return;
  useStoreLocatorIntent.getState().request(args.stacklineSku);
}

export function askConcierge(args: {
  question: string;
  stacklineSku?: string;
}): void {
  if (!args?.question) return;
  const state = useConciergeWidget.getState();
  // Establish a STABLE product context (survives the pendingQuery auto-send
  // clear) so every message in the conversation carries pdpStacklineSku — the
  // signal the backend grounds the PDP answer + family lookup on. On Shopify the
  // theme global wins in ConciergeButton; on the Pages storefront this is the
  // only product source.
  if (args.stacklineSku) state.setActiveStacklineSku(args.stacklineSku);
  state.setPendingQuery({
    query: args.question,
    stacklineSku: args.stacklineSku,
  });
  state.open();
}

/**
 * Open the concierge without forcing a question, optionally maximized
 * (full-page). Used by hosts that land the user directly on a concierge
 * surface (e.g. the App Proxy `/apps/brandclub/concierge` page) rather than
 * a product page with a floating button.
 */
export function openConcierge(args?: {
  maximized?: boolean;
  question?: string;
  stacklineSku?: string;
}): void {
  const state = useConciergeWidget.getState();
  if (args?.question) {
    state.setPendingQuery({
      query: args.question,
      stacklineSku: args.stacklineSku,
    });
  }
  state.open();
  if (args?.maximized) state.setMaximized(true);
}

/**
 * Add a product to the cart, as the in-page Add to bag does. It answers when the add ends: `added` (the bag drawer
 * is open), `retailerPage` (the product cannot be checked out here, and the retailer's page opened), or `refused`
 * with a sentence for the shopper. An add asked for before the cart mounts waits for it.
 */
export function addToBag(args: {
  stacklineSku: string;
  retailerId?: number;
  quantity?: number;
}): Promise<AddToBagOutcome> {
  if (!args?.stacklineSku) return Promise.resolve({ status: "refused", message: ADD_NOT_FINISHED });
  return new Promise((settle) =>
    useAddToBagIntent.getState().request({
      stacklineSku: args.stacklineSku,
      retailerId: args.retailerId,
      quantity: args.quantity ?? 1,
      settle,
    }),
  );
}

export function openBag(): void {
  BagDrawer.show();
}

/**
 * Start this app's checkout of the bag, as the bag drawer's Check out does. It answers `leaving` when the page goes
 * to the checkout, or `refused` with a sentence for the shopper. A start asked for before the cart mounts waits for it.
 */
export function openCheckout(): Promise<CheckoutStartOutcome> {
  return BagDrawer.checkout();
}

export function getCartCount(): number {
  const data = queryClient.getQueryData(activeCartsQueryOptions().queryKey);
  return sumAllItemQuantities(data?.userRetailerShoppingCarts ?? []);
}

export function onCartCountChange(cb: (count: number) => void): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === CachedQueryKeys.UserCart) {
      cb(getCartCount());
    }
  });
}

export function getAuthState(): { isLoggedIn: boolean } {
  const profile = queryClient.getQueryData<UserProfile>([CachedQueryKeys.UserProfile]);
  return { isLoggedIn: !!profile };
}

export function onAuthStateChange(cb: (state: { isLoggedIn: boolean }) => void): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === CachedQueryKeys.UserProfile) {
      cb(getAuthState());
    }
  });
}

export function getHostDomain(): string {
  return HOST_DOMAIN;
}

/**
 * The signed-in shopper, for a host page. This app owns the Amplify session on that page.
 *
 * Before auth setup ends there is no token, and that does not mean "signed out". Both wait for the
 * setup, as the profile and cart reads in this app do.
 */
export async function getAuthorizationHeader(): Promise<Record<string, string>> {
  await authReady;
  return amplifyAuthorizationHeader();
}

/** Redeem a login code and write the session Amplify reads. False when the code or the setup refused. */
export async function redeemAndApplyLoginCode(args: { loginCode: string }): Promise<boolean> {
  await authReady;
  return amplifyRedeemLoginCode(args);
}

export { signOut } from "@brandclub/common-ui";
